// client/src/pages/EditQRCode.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode.react';


function EditQRCode() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [name, setName] = useState('');
  const [type, setType] = useState('url');
  const [content, setContent] = useState('');
  const [shape, setShape] = useState('square');
  const [showName, setShowName] = useState(true);
  const [showURL, setShowURL] = useState(false);
  const [logo, setLogo] = useState(null);
  const [foregroundColor, setForegroundColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  const [textColor, setTextColor] = useState('#000000');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shortId, setShortId] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  // Add vCard state
  const [vCardData, setVCardData] = useState({
    name: '',
    phone: '',
    email: '',
    company: ''
  });

  // Handle vCard field changes
  const handleVCardChange = (field, value) => {
    setVCardData({
      ...vCardData,
      [field]: value
    });
  };
  
  // Function to parse vCard data from the targetUrl
  const parseVCardData = (vcardString) => {
    const result = {
      name: '',
      phone: '',
      email: '',
      company: ''
    };
    
    if (!vcardString || !vcardString.startsWith('BEGIN:VCARD')) return result;
    
    // Extract name (FN field)
    const nameMatch = vcardString.match(/FN:(.*?)(?:\r?\n|$)/);
    if (nameMatch && nameMatch[1]) result.name = nameMatch[1];
    
    // Extract phone (TEL field)
    const phoneMatch = vcardString.match(/TEL:(.*?)(?:\r?\n|$)/);
    if (phoneMatch && phoneMatch[1]) result.phone = phoneMatch[1];
    
    // Extract email (EMAIL field)
    const emailMatch = vcardString.match(/EMAIL:(.*?)(?:\r?\n|$)/);
    if (emailMatch && emailMatch[1]) result.email = emailMatch[1];
    
    // Extract company (ORG field)
    const companyMatch = vcardString.match(/ORG:(.*?)(?:\r?\n|$)/);
    if (companyMatch && companyMatch[1]) result.company = companyMatch[1];
    
    return result;
  };

  // Format vCard data to vCard string format
  const formatVCardData = () => {
    if (type !== 'vcard') return '';
    
    let vcard = 'BEGIN:VCARD\nVERSION:3.0\n';
    
    if (vCardData.name) vcard += `FN:${vCardData.name}\n`;
    if (vCardData.phone) vcard += `TEL:${vCardData.phone}\n`;
    if (vCardData.email) vcard += `EMAIL:${vCardData.email}\n`;
    if (vCardData.company) vcard += `ORG:${vCardData.company}\n`;
    
    vcard += 'END:VCARD';
    
    return vcard;
  };

  const getShowContentLabel = () => {
    switch(type) {
      case 'phone':
        return 'Phone Number';
      case 'email':
        return 'Email Address';
      case 'text':
        return 'Text';
      case 'sms':
        return 'SMS Number';
      case 'vcard':
        return 'Contact Card';
      default:
        return 'URL';
    }
  };
  
  const getContentLabel = () => {
    switch(type) {
      case 'phone':
        return 'Phone Number';
      case 'email':
        return 'Email Address';
      case 'text':
        return 'Text';
      case 'sms':
        return 'SMS Number';
      case 'vcard':
        return 'Contact Card';
      default:
        return 'URL';
    }
  };

  const getFormattedContent = () => {
    if (type === 'vcard') {
      return formatVCardData();
    }
    
    if (!content) return 'https://example.com';
    
    if (type === 'phone') {
      // Format phone with tel: protocol
      const cleanedPhone = content.replace(/(?!^\+)[^\d]/g, '');
      return `tel:${cleanedPhone}`;
    } else if (type === 'url' && !content.startsWith('http://') && !content.startsWith('https://')) {
      // Ensure URLs have proper protocol
      return `https://${content}`;
    }
    
    return content;
  };

  // Fetch QR code data
  useEffect(() => {
    const fetchQRCode = async () => {
      try {
        const response = await fetch(`/api/qrcodes/${id}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch QR code');
        }
        
        const qrCode = await response.json();
        setName(qrCode.qr_name || '');
        setType(qrCode.qrType || 'url');
        
        // Handle vCard data differently
        if (qrCode.qrType === 'vcard') {
          // Parse vCard data from targetUrl
          const parsedVCardData = parseVCardData(qrCode.targetUrl);
          setVCardData(parsedVCardData);
          setContent(''); // Clear content as we're using vCardData
        } else {
          // Extract the raw content from the target URL based on type
          let rawContent = qrCode.targetUrl || '';
          if (qrCode.qrType === 'phone' && rawContent.startsWith('tel:')) {
            rawContent = rawContent.substring(4);
          } else if (qrCode.qrType === 'url' && rawContent.startsWith('https://')) {
            rawContent = rawContent.substring(8);
          }
          
          setContent(rawContent);
        }
        
        setShape(qrCode.shape || 'square');
        
        // Convert to boolean if needed
        const showNameValue = 
          qrCode.showName === true || 
          qrCode.showName === "true" || 
          qrCode.showName === 1 || 
          qrCode.showName === "1";
        
        const showURLValue = 
          qrCode.showURL === true || 
          qrCode.showURL === "true" || 
          qrCode.showURL === 1 || 
          qrCode.showURL === "1";
        
        setShowName(showNameValue);
        setShowURL(showURLValue);
        
        setForegroundColor(qrCode.foregroundColor || '#000000');
        setBackgroundColor(qrCode.backgroundColor || '#FFFFFF');
        setTextColor(qrCode.textColor || '#000000');
        setShortId(qrCode.shortId || '');
        
        if (qrCode.logo) {
          setLogo(qrCode.logo);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching QR code:', err);
        setError('Failed to load QR code');
        setLoading(false);
      }
    };
    
    fetchQRCode();
  }, [id]);
  
  // Update QR code
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const formData = new FormData();
      formData.append('qr_name', name);
      
      // Use the appropriate content based on type
      if (type === 'vcard') {
        formData.append('targetUrl', formatVCardData());
        
        // Also append individual vCard fields for server-side processing
        formData.append('vCardName', vCardData.name || '');
        formData.append('vCardPhone', vCardData.phone || '');
        formData.append('vCardEmail', vCardData.email || '');
        formData.append('vCardCompany', vCardData.company || '');
      } else {
        formData.append('targetUrl', getFormattedContent()); // Use formatted content
      }
      
      formData.append('qrType', type);
      formData.append('shape', shape);
      formData.append('foregroundColor', foregroundColor);
      formData.append('backgroundColor', backgroundColor);
      formData.append('textColor', textColor);
      formData.append('showName', showName.toString()); // Convert to string
      formData.append('showURL', showURL.toString()); // Convert to string
      
      if (logoFile) {
        formData.append('logo', logoFile);
      }
      
      const response = await fetch(`/api/qrcodes/${id}`, {
        method: 'PUT',
        body: formData,
      });
      
      if (response.ok) {
        alert('QR code updated successfully!');
        navigate('/');
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error}`);
      }
    } catch (err) {
      console.error('Error updating QR code:', err);
      alert('Failed to update QR code');
    }
  };
  
  // Update logo
  const handleLogoChange = (e) => {
    if (e.target.files[0]) {
      setLogoFile(e.target.files[0]);
      setLogo(URL.createObjectURL(e.target.files[0]));
    }
  };
  
  // Configure imageSettings for QRCode if logo is set
  const getQRCodeImageSettings = () => {
    if (logo) {
      return {
        src: logo,
        width: 40,
        height: 40,
        excavate: true
      };
    }
    return undefined;
  };
  
  if (loading) {
    return <div className="text-center py-10">Loading...</div>;
  }
  
  if (error) {
    return <div className="bg-red-100 text-red-700 p-4 rounded-lg">{error}</div>;
  }

  return (
    <div className="mt-6 max-w-4xl mx-auto px-3 sm:px-0">
      <h1 className="mt-6 text-3xl font-bold mb-2">Edit QR Code</h1>
      <div className="grid grid-cols-1 md:grid-cols-8 gap-4">
        {/* Form section - MADE RESPONSIVE HERE */}
        <div className="md:col-span-5 bg-white rounded-lg shadow-sm p-3 md:p-5 mb-6">
          <form onSubmit={handleSubmit} encType="multipart/form-data">
            <div className="mb-1">
              <label className="block mt-5 text-m text-gray-700 font-semibold mb-1">Update Name:</label>
              <input
                type="text"
                className="w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Come up with a name to help identify your QR code"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="flex items-center block text-sm text-gray-700 font-medium mb-8">
              <input
                type="checkbox"
                id="showName"
                className="mr-2"
                checked={showName}
                onChange={(e) => setShowName(e.target.checked)}
              />
              <label htmlFor="showName">Show Name</label>
            </div>

            {/* Conditional rendering based on QR code type */}
            {type === 'vcard' ? (
              // vCard fields for editing
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200 mb-4">
                <h3 className="font-semibold text-gray-700">Update Contact Information</h3>
                
                <div>
                  <label className="block text-sm text-gray-700 font-medium mb-1">Full Name:</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="John Doe"
                    value={vCardData.name}
                    onChange={(e) => handleVCardChange('name', e.target.value)}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 font-medium mb-1">Phone:</label>
                  <input
                    type="tel"
                    className="w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+1 (123) 456-7890"
                    value={vCardData.phone}
                    onChange={(e) => handleVCardChange('phone', e.target.value)}
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 font-medium mb-1">Email:</label>
                  <input
                    type="email"
                    className="w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="john.doe@example.com"
                    value={vCardData.email}
                    onChange={(e) => handleVCardChange('email', e.target.value)}
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 font-medium mb-1">Company (optional):</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Company Name"
                    value={vCardData.company}
                    onChange={(e) => handleVCardChange('company', e.target.value)}
                  />
                </div>
              </div>
            ) : (
              // Standard content field for other QR code types
              <div className="mb-4">
                <label className="block mt-5 text-m text-gray-700 font-semibold mb-1">
                  Update {getContentLabel()}:
                </label>
                <p className="text-sm text-gray-400 mb-1">Existing QR codes will relink to the updated <label htmlFor="showURL">{getShowContentLabel()}</label></p>

                <input
                  type={type === 'phone' ? 'tel' : 'text'}
                  className="w-full px-4 py-2 border rounded font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={type === 'url' ? 'www.mywebsite.com' : type === 'phone' ? '+1234567890' : 'Your text here'}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  required
                />
              </div>
            )}

            {/* Only show the URL checkbox for non-vcard types */}
            {type !== 'vcard' && (
              <div className="flex items-center text-gray-700 font-medium mb-4">
                <input
                  type="checkbox"
                  id="showURL"
                  className="mr-2"
                  checked={showURL}
                  onChange={(e) => setShowURL(e.target.checked)}
                />
                <label htmlFor="showURL">Show {getShowContentLabel()}</label>
              </div>
            )}

            <label className="pt-3 block mt-5 text-m text-gray-700 font-semibold mb-1">Customize colors:</label>

            {/* Color selectors - MADE RESPONSIVE HERE */}
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm text-gray-700 font-medium mb-2">QR Code:</label>
                <div className="flex items-center">
                  <input
                    type="color"
                    className="w-10 h-10 font-medium border rounded mr-2"
                    value={foregroundColor}
                    onChange={(e) => setForegroundColor(e.target.value)}
                  />
                  <input
                    type="text"
                    className="flex-grow w-full h-10 text-sm font-medium px-3 py-2 border rounded"
                    value={foregroundColor}
                    onChange={(e) => setForegroundColor(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-700 font-medium mb-2">Background:</label>
                <div className="flex items-center">
                  <input
                    type="color"
                    className="w-10 h-10 text-sm font-medium border rounded mr-1"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                  />
                  <input
                    type="text"
                    className="w-full h-10 text-sm flex-grow font-medium px-3 py-2 border rounded"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                  />
                </div>
              </div>
                
              <div>
                <label className="block text-sm text-gray-700 font-medium mb-2">Text:</label>
                <div className="flex items-center">
                  <input
                    type="color"
                    className="w-10 h-10 font-medium border rounded mr-1"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                  />
                  <input
                    type="text"
                    className="w-full h-10 flex-grow text-sm font-medium px-3 py-2 border rounded"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="pt-3 block mt-5 text-m text-gray-700 font-semibold mb-2">Add Logo or Image:</label>
              <input
                type="file"
                accept="image/*"
                className="w-full"
                onChange={handleLogoChange}
                name="logo"
              />
            </div>

            <div className="grid grid-cols-2 space-x-4 mt-4">
              <button
                type="submit"
                className="w-full bg-blue-600 mt-2 text-white py-3 rounded font-medium hover:bg-blue-900 transition duration-200"
              >
                Save Changes
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="w-full bg-white border-2 mt-2 text-gray-900 py-3 rounded font-medium hover:bg-gray-200 transition duration-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
   
        <div className="md:col-span-3 flex flex-col items-center">
          {/* QR Code Preview with styling */}
          <div 
            className={`qr-container p-10 ${shape === 'circle' ? 'rounded-full' : shape === 'rounded' ? 'rounded-2xl' : ''}`}
            style={{ 
              backgroundColor: backgroundColor || '#f1f1f4',
              ...(shape === 'circle' ? {
                width: '310px',  
                height: '310px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              } : {})
            }}
          >
            <div className="relative">
              <QRCode 
                value={getFormattedContent()} 
                size={150}
                fgColor={foregroundColor}
                bgColor="transparent"
                level="H"
                renderAs="svg"
                imageSettings={getQRCodeImageSettings()}
              />
              
              {/* Logo overlay */}
              {logo && (
                <div className="absolute inset-0 flex items-center justify-center">
                  {/* Logo background */}
                  <div 
                    className={`${
                      shape === 'circle' ? 'rounded-full' : 
                      shape === 'rounded' ? 'rounded-lg' : 
                      'rounded-none'
                    }`}
                    style={{
                      width: '60px', 
                      height: '60px', 
                      backgroundColor: backgroundColor,
                      position: 'absolute',
                      zIndex: 5
                    }}
                  />
                  
                  {/* Logo image */}
                  <img 
                    src={logo} 
                    alt="Logo" 
                    style={{
                      width: '40px',
                      height: '40px',
                      zIndex: 10,
                      position: 'absolute'
                    }}
                  />
                </div>
              )}
            </div>

            {/* Show name if selected */}
            {showName && name && (
              <div 
                className="text-center text-lg font-bold mt-3" 
                style={{ color: textColor || "#000000" }}
              >
                {name}
              </div>
            )}
            
            {/* Show URL if selected - for non-vCard types */}
            {showURL && content && type !== 'vcard' && (
              <div 
                className="text-center text-m font-semibold"
                style={{ color: textColor || "#000000" }}
              >
                {getFormattedContent()}
              </div>
            )}
            
            {/* For vCard, show a simplified preview */}
            {type === 'vcard' && showURL && vCardData.name && (
              <div 
                className="text-center text-m font-semibold"
                style={{ color: textColor || "#000000" }}
              >
                Contact: {vCardData.name}
              </div>
            )}
          </div>
          <h2 className="mt-1 text-sm text-gray-400 font-medium mb-2">Preview</h2>

          <div className="mt-6 w-full">
            <h3 className="block mt-5 text-m text-gray-700 font-semibold mb-2">Select one:</h3>
            {/* Shape selector - MADE RESPONSIVE HERE */}
            <div className="grid grid-cols-3 gap-2 md:gap-4">
              <div 
                className={`flex flex-col items-center cursor-pointer p-2 md:p-3 border rounded-lg ${shape === 'square' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                onClick={() => setShape('square')}
              >
                <div className="bg-gray-200 w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mb-2">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-300"></div>
                </div>
                <span className="block text-xs md:text-sm text-gray-700 font-medium text-center mb-1">Square</span>
                <span className="text-xs text-gray-500 hidden md:inline">(Preview)</span>
              </div>
              
              <div 
                className={`flex flex-col items-center cursor-pointer p-2 md:p-3 border rounded-lg ${shape === 'rounded' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                onClick={() => setShape('rounded')}
              >
                <div className="bg-gray-200 w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mb-2 rounded-lg">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-300 rounded-lg"></div>
                </div>
                <span className="block text-xs md:text-sm text-gray-700 font-medium text-center mb-1">Rounded</span>
                <span className="text-xs text-gray-500 hidden md:inline">(Preview)</span>
              </div>
              
              <div 
                className={`flex flex-col items-center cursor-pointer p-2 md:p-3 border rounded-lg ${shape === 'circle' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                onClick={() => setShape('circle')}
              >
                <div className="bg-gray-200 w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mb-2 rounded-full">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-300 rounded-full"></div>
                </div>
                <span className="block text-xs md:text-sm text-gray-700 font-medium text-center mb-1">Circle</span>
                <span className="text-xs text-gray-500 hidden md:inline">(Preview)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditQRCode;